"use client";

import { m as motion } from "framer-motion";
import { ShieldAlert, Lightbulb, AlertTriangle } from "lucide-react";
import { fadeInUp, defaultViewport, defaultTransition, fastTransition } from "@/lib/motion";

export const AIRealitySection = () => {
  const realities = [
    {
      icon: <ShieldAlert className="w-12 h-12" />,
      title: "Concerned About Real Risks",
      color: "text-red-500",
      bgColor: "bg-red-500/10",
      concerns: [
        "How do we know our data are safe?",
        "What are the legal implications?",
        "Can we trust AI outputs?",
      ],
    },
    {
      icon: <Lightbulb className="w-12 h-12" />,
      title: "Fighting FOMO and Skepticism",
      color: "text-primary",
      bgColor: "bg-primary/10",
      concerns: [
        "My team is not sure where to start.",
        "I'm worried we're falling behind with AI.",
        "Employees are anxious about AI in the workplace.",
      ],
    },
    {
      icon: <AlertTriangle className="w-12 h-12" />,
      title: "Mindful of Unmet Expectations",
      color: "text-secondary",
      bgColor: "bg-secondary/10",
      concerns: [
        "We invested in AI tools that nobody uses.",
        "Our AI projects aren't delivering expected results.",
        "We're drowning in vendor promises.",
      ],
    },
  ];

  return (
    <div className="section-inner">
      <motion.div
        initial={fadeInUp.initial}
        whileInView={fadeInUp.animate}
        viewport={defaultViewport}
        transition={defaultTransition}
        className="flex flex-col items-center justify-center text-center mb-16"
      >
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-background mb-8">
          Which AI Reality Are You Stuck In?
        </h2>

        <div className="bg-background rounded-2xl p-8 max-w-3xl mx-auto shadow-lg">
          <p className="text-2xl md:text-3xl font-semibold text-primary mb-4">
            81% of HR pros say they're already exploring or using&nbsp;AI
          </p>
          <p className="text-lg md:text-xl text-foreground/70">
            but most are stuck in these challenging situations:
          </p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        {realities.map((reality, index) => (
          <motion.div
            key={reality.title}
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={defaultViewport}
            transition={{ ...defaultTransition, delay: index * 0.1 }}
            className="bg-background rounded-2xl p-8 hover-lift shadow-lg"
          >
            <div className="flex justify-center mb-6">
              <div className={`w-20 h-20 rounded-2xl ${reality.bgColor} flex items-center justify-center ${reality.color}`}>
                {reality.icon}
              </div>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-6 text-center">
              {reality.title}
            </h3>
            <ul className="space-y-3">
              {reality.concerns.map((concern, i) => (
                <li key={i} className="text-foreground/70 text-left pl-4 border-l-2 border-primary/30">
                  "{concern}"
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={fadeInUp.initial}
        whileInView={fadeInUp.animate}
        viewport={defaultViewport}
        transition={{ ...defaultTransition, delay: 0.3 }}
        className="bg-background rounded-2xl p-12 text-center max-w-4xl mx-auto shadow-lg"
      >
        <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
          There's a Better Way Forward
        </h3>
        <p className="text-foreground/70 text-lg md:text-xl mb-4 leading-relaxed">
          HR pros are curious about AI, but concerns hold them back.
          Companies have yet to provide much guidance or support. 
          HR pros have to learn AI skills on their own.
        </p>
        <p className="text-foreground/70 text-lg md:text-xl leading-relaxed">
          If you work in people analytics, talent acquisition, or employee experience... 
          there's a better way to adopt AI.
        </p>
      </motion.div>
    </div>
  );
};
